import { useCallback, useEffect, useState } from "react";
import Help from "./components/Help/Help";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";
import NotificationCenter from "./components/Notifications/NotificationCenter";
import ReaderShell from "./components/ReaderShell/ReaderShell";
import UnauthorizedDomain, {
  isCorsBlockedValidateError,
} from "./components/UnauthorizedDomain/UnauthorizedDomain";
import useReaderStore from "./context/readerStore";
import useDevToolsProtection from "./hooks/useDevToolsProtection";
import useDocumentTitle from "./hooks/useDocumentTitle";
import useKeyboardShortcuts from "./hooks/useKeyboardShortcuts";
import i18n from "./i18n";
import { devDebug, devLog } from "./logger";
import "./services/apiInterceptor";
import { clearAiSessionCaches } from "./services/aiService";
import { getEffectiveStartPage } from "./services/drmPolicyService";
import {
  isLegacySavedEpubPage,
  loadEpubDocument,
  resolveApiInitialStartPage,
} from "./services/epubService";
import {
  fetchAccessData,
  fetchArrayBufferFromUrl,
  fetchJsonFromUrl,
  getPresignedUrl,
  getValidRstToken,
  normalizeGrantIdForAccess,
  setIgpBaseUrl,
  validateContentServerToken,
} from "./services/igpService";
import { loadPdfDocument } from "./services/pdfService";
import "./styles/global.css";

export default function App() {
  const {
    theme,
    setDocument,
    setTotalPages,
    setError,
    setLoading,
    setAnnotationData,
    setAnnotationLoading,
    setAnnotationError,
    setDrmPolicy,
    setInitialView,
  } = useReaderStore();

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initMessage, setInitMessage] = useState(() =>
    i18n.t("loading.initializing"),
  );
  const [isUnauthorizedDomain, setIsUnauthorizedDomain] = useState(false);
  useKeyboardShortcuts();
  useDevToolsProtection();
  useDocumentTitle();

  const toggleHelp = () => {
    setIsHelpOpen(!isHelpOpen);
  };

  const fetchDocumentJson = useCallback(
    async (catToken, documentId, fileName) => {
      try {
        const signRes = await getPresignedUrl(catToken, documentId, fileName);
        const data = await fetchJsonFromUrl(signRes.presigned_url);
        devLog(fileName, JSON.stringify(data, null, 2));
        return data;
      } catch (error) {
        return null;
      }
    },
    [],
  );

  const buildPdfMetadata = (processedMetadata, richMetadata) => {
    const processed = processedMetadata || {};
    const rich = richMetadata || {};
    const processedContentType = (
      processed.content_type ||
      processed.format ||
      ""
    )
      .toString()
      .toLowerCase();
    const isProcessedEpub = processedContentType === "epub";
    const tableOfContents = Array.isArray(rich.metadata?.table_of_contents)
      ? rich.metadata.table_of_contents
      : Array.isArray(processed.table_of_contents)
        ? processed.table_of_contents
        : [];

    return {
      ...processed,
      ...rich,
      title:
        rich.title ||
        processed.title ||
        rich.metadata?.bibliographic_data?.title ||
        processed.metadata?.bibliographic_data?.title ||
        "",
      total_pages:
        (isProcessedEpub ? Number(processed.total_pages) : 0) ||
        Number(rich.total_pages) ||
        Number(processed.total_pages) ||
        Number(processed.end_page) ||
        Number(rich.end_page) ||
        0,
      // metadata.json should have priority for first_page over processed metadata.
      first_page:
        Number(rich.first_page) || Number(processed.first_page) || null,
      // Keep legacy start_page for backward compatibility.
      start_page: Number(rich.start_page) || Number(processed.start_page) || 1,
      end_page:
        Number(rich.end_page) ||
        Number(processed.end_page) ||
        Number(rich.total_pages) ||
        Number(processed.total_pages) ||
        1,
      table_of_contents: tableOfContents,
      source_metadata: processed,
      rich_metadata: rich,
      ...(isProcessedEpub
        ? {
            content_type: processed.content_type || "epub",
            total_character_count: processed.total_character_count,
            total_chapters:
              Number(processed.total_chapters) ||
              (Array.isArray(processed.chapters)
                ? processed.chapters.length
                : 0),
            chapters: Array.isArray(processed.chapters)
              ? processed.chapters
              : rich.chapters,
          }
        : {}),
    };
  };

  const getPdfDocumentName = (metadata, fallbackName = "") => {
    if (!metadata) return fallbackName;
    return (
      metadata.title ||
      metadata.metadata?.bibliographic_data?.title ||
      fallbackName
    );
  };

  // Normalize metadata for PDF and EPUB. For EPUBs that provide the
  // new chapter-oriented structure, map it through while keeping
  // backward-compatibility for existing PDF-style metadata.
  const normalizeMetadata = (rawMetadata, isEpub) => {
    if (!rawMetadata) return { normalized: {}, startPage: 1, endPage: 1 };

    // Default behavior (PDF or legacy): use start_page/end_page/total_pages
    if (!isEpub || (!rawMetadata.chapters && !rawMetadata.total_chapters)) {
      const startPage = Number(rawMetadata.start_page) || 1;
      const endPage =
        Number(rawMetadata.end_page) ||
        Number(rawMetadata.total_pages) ||
        startPage;
      const normalized = {
        ...rawMetadata,
        total_pages: Number(rawMetadata.total_pages) || endPage,
      };
      return { normalized, startPage, endPage };
    }

    // EPUB with new chapter-oriented metadata structure
    // Expecting fields: content_type: 'epub', total_pages, total_chapters, chapters: [] (each with start_page, end_page, page_count)
    const startPage = 1;
    let endPage = Number(rawMetadata.total_pages) || 0;
    if (
      !endPage &&
      Array.isArray(rawMetadata.chapters) &&
      rawMetadata.chapters.length > 0
    ) {
      const lastChapter = rawMetadata.chapters[rawMetadata.chapters.length - 1];
      endPage = Number(lastChapter?.end_page) || endPage;
    }
    if (!endPage) endPage = 1;
    const normalized = {
      ...rawMetadata,
      total_pages: Number(rawMetadata.total_pages) || endPage,
      total_chapters:
        Number(rawMetadata.total_chapters) ||
        (Array.isArray(rawMetadata.chapters) ? rawMetadata.chapters.length : 0),
    };

    return { normalized, startPage, endPage };
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const fetchAndSetAnnotations = useCallback(
    async (grantId, agtToken, rstToken = null) => {
      try {
        setAnnotationLoading("bookmarks", true);
        setAnnotationLoading("highlights", true);
        setAnnotationLoading("notes", true);
        setAnnotationError("bookmarks", null);
        setAnnotationError("highlights", null);
        setAnnotationError("notes", null);

        let token;
        if (rstToken) {
          token = rstToken;
        } else if (agtToken) {
          token = await getValidRstToken(grantId, agtToken);
        } else {
          throw new Error(
            "fetchAndSetAnnotations requires either agtToken or rstToken",
          );
        }

        const accessData = await fetchAccessData(grantId, token, [
          "bookmarks",
          "highlights",
          "notes",
          "freehand_drawing",
          "underline",
          "textbox",
          "drm_policy",
          "initial_view",
        ]);
        // Set reader language from access API if provided. Default to English.
        const apiLangRaw =
          accessData?.display_language || accessData?.displayLanguage || "";

        const mapDisplayLanguage = (val) => {
          if (!val || typeof val !== "string") return null;
          const v = val.trim();
          const map = {
            en: "en",
            "en-US": "en",
            el: "el",
            "el-GR": "el",
            es: "es",
            ru: "ru",
            th: "th",
            tr: "tr",
            zcn: "zcn",
            zh_TW: "ztw",
            ztw: "ztw",
            ja: "ja",
            ko: "ko",
            my: "my",
            hi: "hi",
            vi: "vi",
            fil: "fil",
            tl: "fil",
          };
          // Normalize casing and separators
          const key = v.replace(/_/g, "-").toLowerCase();
          // try direct match first (case sensitive keys in map)
          if (map[v]) return map[v];
          // try lowercased key variants
          for (const k of Object.keys(map)) {
            if (k.toLowerCase() === key) return map[k];
          }
          return null;
        };

        const mappedLang = mapDisplayLanguage(apiLangRaw) || "en";
        if (i18n && i18n.language !== mappedLang) {
          try {
            i18n.changeLanguage(mappedLang);
          } catch (e) {
            console.warn("Failed to change language to", mappedLang, e);
          }
        }
        const initialView = accessData?.initial_view || {};
        devLog("Data from Annotations API:", accessData);
        const {
          page_display_style: pageDisplayStyle,
          start_page: initialStartPage,
        } = initialView;
        const bookmarks = accessData?.bookmarks?.data || [];
        const highlights = accessData?.highlights?.data || [];
        const notes = accessData?.notes?.data || [];
        const drawings = accessData?.freehand_drawing?.data || [];
        const underlines = accessData?.underline?.data || [];
        // const textboxes = accessData?.textbox?.data || [];

        if (accessData?.drm_policy) {
          devDebug("DRM policy from accessData:", accessData.drm_policy);
          setDrmPolicy(accessData.drm_policy);
        }

        // Prefer API initial_view.start_page first, then metadata.json start_page, then localStorage fallback.
        const metadataStartPageRaw =
          useReaderStore.getState().metadata?.start_page;
        const metadataStartPage =
          metadataStartPageRaw != null &&
          !Number.isNaN(Number(metadataStartPageRaw))
            ? Number(metadataStartPageRaw)
            : null;

        const currentMetadata = useReaderStore.getState().metadata;
        const apiStartPage = resolveApiInitialStartPage(
          initialStartPage,
          currentMetadata,
        );
        const currentDocumentId = useReaderStore.getState().documentId;
        const localStorageKey = currentDocumentId
          ? `igp_reader_state_${currentDocumentId}`
          : null;
        const localRaw = localStorageKey
          ? localStorage.getItem(localStorageKey)
          : null;

        let startPageToApply =
          apiStartPage != null && apiStartPage > 0
            ? apiStartPage
            : metadataStartPage != null && metadataStartPage > 1
              ? metadataStartPage
              : null;

        try {
          if (localRaw) {
            const local = JSON.parse(localRaw);
            const savedPage =
              local.currentPage != null ? Number(local.currentPage) : null;
            const useSaved =
              (startPageToApply == null || startPageToApply <= 1) &&
              !isLegacySavedEpubPage(savedPage, currentMetadata);
            if (savedPage >= 1 && useSaved) {
              startPageToApply = savedPage;
            }
          }
        } catch (e) {
          // ignore parse error
        }

        const hasViewingRules =
          !!accessData?.drm_policy?.viewing_rules?.page_ranges?.length;
        const effectiveStartPage = hasViewingRules
          ? getEffectiveStartPage(accessData.drm_policy, startPageToApply)
          : startPageToApply != null
            ? startPageToApply
            : 1;

        if (pageDisplayStyle != null || effectiveStartPage != null) {
          setInitialView(pageDisplayStyle, effectiveStartPage);
        }

        setAnnotationData("bookmarks", bookmarks);
        setAnnotationData("highlights", highlights);
        setAnnotationData("notes", notes);
        setAnnotationData("freehand_drawing", drawings);
        setAnnotationData("underlines", underlines);
        // setAnnotationData("textboxes", textboxes);
      } catch (err) {
        console.error("Error fetching annotations:", err);
        setAnnotationError(
          "bookmarks",
          err?.message || "Failed to fetch bookmarks",
        );
        setAnnotationError(
          "highlights",
          err?.message || "Failed to fetch highlights",
        );
        setAnnotationError("notes", err?.message || "Failed to fetch notes");
      } finally {
        setAnnotationLoading("bookmarks", false);
        setAnnotationLoading("highlights", false);
        setAnnotationLoading("notes", false);
      }
    },
    [
      setAnnotationData,
      setAnnotationLoading,
      setAnnotationError,
      setDrmPolicy,
      setInitialView,
    ],
  );

  const initializeReaderFromSession = useCallback(
    async (savedSession) => {
      setIsInitializing(true);
      setInitMessage(i18n.t("loading.restoringSession"));
      setLoading(true);
      setError(null);

      try {
        const {
          grantId: rawGrantId,
          documentId,
          rstToken,
          catToken,
          baseUrl,
        } = savedSession;
        const grantId = normalizeGrantIdForAccess(rawGrantId);

        if (!rstToken) {
          throw new Error("No RST token found in saved session.");
        }

        if (baseUrl && typeof baseUrl === "string" && baseUrl.trim() !== "") {
          setIgpBaseUrl(baseUrl.trim());
        }

        if (!catToken) {
          throw new Error("No CAT token found in saved session.");
        }
        const finalCatToken = catToken;

        setInitMessage(i18n.t("loading.loadingDocument"));
        const processedMetadata = await fetchDocumentJson(
          finalCatToken,
          documentId,
          "pmetadata.json",
        );
        const richMetadata = await fetchDocumentJson(
          finalCatToken,
          documentId,
          "metadata.json",
        );
        const metadata =
          processedMetadata || richMetadata
            ? buildPdfMetadata(processedMetadata || {}, richMetadata || {})
            : {};
        const fileNameLower = (
          getPdfDocumentName(metadata, "document") || "document"
        ).toLowerCase();
        const contentType = (metadata?.content_type || metadata?.format || "")
          .toString()
          .toLowerCase();
        const isEpub =
          contentType === "epub" || fileNameLower.endsWith(".epub");

        const {
          normalized: normalizedMetadata,
          startPage,
          endPage,
        } = normalizeMetadata(metadata, isEpub);

        // If legacy metadata is missing page info, keep the old warning behaviour
        if (!isEpub && !metadata?.end_page && !metadata?.total_pages) {
          console.warn(
            "⚠️ Metadata missing end_page and total_pages. Defaulting document to single page. Chunking may not work correctly.",
          );
        }

        const extension = isEpub ? ".epub" : ".pdf";
        let firstChunkName;
        let initialChapterNumber = 1;

        if (isEpub && Array.isArray(normalizedMetadata.chapters)) {
          const chapterObj = normalizedMetadata.chapters.find((c) => {
            const s = Number(c.start_page) || 0;
            const e = Number(c.end_page) || 0;
            return startPage >= s && startPage <= e;
          });
          initialChapterNumber = chapterObj
            ? Number(chapterObj.chapter_number) || 1
            : 1;
          firstChunkName = `pages/chapter_${initialChapterNumber}${extension}`;
        } else if (isEpub) {
          firstChunkName = `pages/chapter_1${extension}`;
        } else {
          const chunkSize = 5;
          const firstEnd = Math.min(startPage + chunkSize - 1, endPage);
          firstChunkName = `pages/${startPage}-${firstEnd}${extension}`;
        }

        const firstChunkSignRes = await getPresignedUrl(
          finalCatToken,
          documentId,
          firstChunkName,
        );
        const buffer = await fetchArrayBufferFromUrl(
          firstChunkSignRes.presigned_url,
        );

        const pdfMetadata =
          !isEpub && Object.keys(metadata).length > 0
            ? metadata
            : normalizedMetadata;

        if (isEpub) {
          const epub = await loadEpubDocument(buffer);
          // EPUB title lives on metadata.json (`richMetadata.title`).
          setDocument(
            epub,
            "epub",
            richMetadata?.title || getPdfDocumentName(metadata),
            {
              isChunked: true,
              metadata: normalizedMetadata,
              documentId,
              loadedChunks: { [initialChapterNumber]: epub },
              initialChapterNumber,
            },
          );
        } else {
          const pdf = await loadPdfDocument(buffer);
          setDocument(
            pdf,
            "pdf",
            getPdfDocumentName(metadata, metadata.title || ""),
            {
              isChunked: true,
              metadata: pdfMetadata,
              documentId,
              loadedChunks: {
                [Number.isFinite(Number(startPage)) && Number(startPage) > 0
                  ? Math.floor((Number(startPage) - 1) / 5)
                  : 0]: pdf,
              },
            },
          );
        }

        setTotalPages(normalizedMetadata.total_pages || endPage || 0);

        setInitMessage(i18n.t("loading.finalizing"));
        await fetchAndSetAnnotations(grantId, null, rstToken);
        setIsInitializing(false);
      } catch (err) {
        // Clear session if restore fails
        sessionStorage.removeItem("igp_session");
        clearAiSessionCaches();
        // Global interceptor handles 401 and rst_token_expired errors
        setError(err?.message || i18n.t("errors.failedToRestoreSession"));

        setIsInitializing(false);
      } finally {
        setLoading(false);
      }
    },
    [
      setDocument,
      setTotalPages,
      setError,
      setLoading,
      fetchAndSetAnnotations,
      fetchDocumentJson,
    ],
  );

  const initializeReaderFromToken = useCallback(
    async (token) => {
      setIsInitializing(true);
      setInitMessage(i18n.t("loading.authenticating"));
      setLoading(true);
      setError(null);
      setIsUnauthorizedDomain(false);

      try {
        let validation;
        try {
          validation = await validateContentServerToken(token);
        } catch (validateError) {
          // Backend CORS deny has no response body; only then show domain UI.
          if (isCorsBlockedValidateError(validateError)) {
            setIsUnauthorizedDomain(true);
            setIsInitializing(false);
            setLoading(false);
            return;
          }
          throw validateError;
        }

        const rstToken =
          validation?.rst || validation?.rst_token || validation?.rstToken;
        const catToken =
          validation?.cat_token ||
          validation?.catToken ||
          validation?.data?.cat_token;
        const baseUrl =
          validation?.base_url ||
          validation?.baseUrl ||
          validation?.user_data?.baseUrl ||
          "";
        const userData = validation.user_data?.data || {};

        const grantId = normalizeGrantIdForAccess(
          userData.grant_id || userData.grantId,
        );
        const documentId = userData.document_id || userData.documentId;
        const fileName = userData.file_name || userData.fileName;

        if (baseUrl) {
          setIgpBaseUrl(baseUrl);
        }

        if (!grantId || !documentId || !rstToken || !catToken) {
          setError(
            "Token validation response missing required fields (grantId, documentId, rstToken, catToken).",
          );
          setLoading(false);
          setIsInitializing(false);
          return;
        }

        setInitMessage(i18n.t("loading.loadingDocument"));
        const processedMetadata = await fetchDocumentJson(
          catToken,
          documentId,
          "pmetadata.json",
        );
        const richMetadata = await fetchDocumentJson(
          catToken,
          documentId,
          "metadata.json",
        );
        const metadata =
          processedMetadata || richMetadata
            ? buildPdfMetadata(processedMetadata || {}, richMetadata || {})
            : {};
        const fileNameLower = (
          fileName ||
          getPdfDocumentName(metadata, metadata?.title || "document") ||
          "document"
        ).toLowerCase();
        const contentType = (metadata?.content_type || metadata?.format || "")
          .toString()
          .toLowerCase();
        const isEpub =
          contentType === "epub" || fileNameLower.endsWith(".epub");

        const {
          normalized: normalizedMetadata,
          startPage,
          endPage,
        } = normalizeMetadata(metadata, isEpub);

        // If legacy metadata is missing page info, keep the old warning behaviour
        if (!isEpub && !metadata?.end_page && !metadata?.total_pages) {
          console.warn(
            "⚠️ Metadata missing end_page and total_pages. Defaulting document to single page. Chunking may not work correctly.",
          );
        }

        const extension = isEpub ? ".epub" : ".pdf";
        let firstChunkName;
        let chapterNumber = 1;
        let chunkIndex = 0;
        if (isEpub && Array.isArray(normalizedMetadata.chapters)) {
          // Find the chapter that contains the startPage
          const chapterObj = normalizedMetadata.chapters.find((c) => {
            const s = Number(c.start_page) || 0;
            const e = Number(c.end_page) || 0;
            return startPage >= s && startPage <= e;
          });
          chapterNumber = chapterObj
            ? Number(chapterObj.chapter_number) || 1
            : 1;
          firstChunkName = `pages/chapter_${chapterNumber}${extension}`;
        } else if (isEpub) {
          firstChunkName = `pages/chapter_1${extension}`;
        } else {
          const chunkSize = 5;
          chunkIndex = Math.floor((startPage - 1) / chunkSize);
          const chunkStart = chunkIndex * chunkSize + 1;
          const chunkEnd = Math.min((chunkIndex + 1) * chunkSize, endPage);
          firstChunkName = `pages/${chunkStart}-${chunkEnd}${extension}`;
        }

        const firstChunkSignRes = await getPresignedUrl(
          catToken,
          documentId,
          firstChunkName,
        );
        const buffer = await fetchArrayBufferFromUrl(
          firstChunkSignRes.presigned_url,
        );

        const pdfMetadata =
          !isEpub && Object.keys(metadata).length > 0
            ? metadata
            : normalizedMetadata;

        if (isEpub) {
          const epub = await loadEpubDocument(buffer);
          // EPUB title lives on metadata.json (`richMetadata.title`).
          setDocument(
            epub,
            "epub",
            richMetadata?.title ||
              getPdfDocumentName(metadata, fileName || ""),
            {
              isChunked: true,
              metadata: normalizedMetadata,
              documentId,
              loadedChunks: { [chapterNumber]: epub },
              initialChapterNumber: chapterNumber,
            },
          );
        } else {
          const pdf = await loadPdfDocument(buffer);
          setDocument(
            pdf,
            "pdf",
            getPdfDocumentName(metadata, fileName || metadata?.title || ""),
            {
              isChunked: true,
              metadata: pdfMetadata,
              documentId,
              loadedChunks: { [chunkIndex]: pdf },
            },
          );
        }

        setTotalPages(normalizedMetadata.total_pages || endPage || 0);

        const sessionData = {
          grantId,
          documentId,
          catToken,
          rstToken,
          baseUrl,
          savedAt: Date.now(),
        };

        sessionStorage.setItem("igp_session", JSON.stringify(sessionData));

        setInitMessage(i18n.t("loading.finalizing"));

        try {
          setAnnotationLoading("bookmarks", true);
          setAnnotationLoading("highlights", true);
          setAnnotationLoading("notes", true);
          setAnnotationError("bookmarks", null);
          setAnnotationError("highlights", null);
          setAnnotationError("notes", null);

          const accessData = userData;
          const apiLangRaw =
            accessData?.display_language || accessData?.displayLanguage || "";

          const mapDisplayLanguage = (val) => {
            if (!val || typeof val !== "string") return null;
            const v = val.trim();
            const map = {
              en: "en",
              "en-US": "en",
              el: "el",
              "el-GR": "el",
              es: "es",
              ru: "ru",
              th: "th",
              tr: "tr",
              zcn: "zcn",
              zh_TW: "ztw",
              ztw: "ztw",
              ja: "ja",
              ko: "ko",
              my: "my",
              hi: "hi",
              vi: "vi",
              fil: "fil",
              tl: "fil",
            };
            const key = v.replace(/_/g, "-").toLowerCase();
            if (map[v]) return map[v];
            for (const k of Object.keys(map)) {
              if (k.toLowerCase() === key) return map[k];
            }
            return null;
          };

          const mappedLang = mapDisplayLanguage(apiLangRaw) || "en";
          if (i18n && i18n.language !== mappedLang) {
            try {
              i18n.changeLanguage(mappedLang);
            } catch (e) {
              console.warn("Failed to change language to", mappedLang, e);
            }
          }

          const initialView = accessData?.initial_view || {};
          devLog("Data from validate API:", accessData);
          const {
            page_display_style: pageDisplayStyle,
            start_page: initialStartPage,
          } = initialView;
          const bookmarks = accessData?.bookmarks?.data || [];
          const highlights = accessData?.highlights?.data || [];
          const notes = accessData?.notes?.data || [];
          const drawings = accessData?.freehand_drawing?.data || [];
          const underlines = accessData?.underline?.data || [];

          if (accessData?.drm_policy) {
            devDebug("DRM policy from accessData:", accessData.drm_policy);
            setDrmPolicy(accessData.drm_policy);
          }

          const metadataStartPageRaw =
            useReaderStore.getState().metadata?.start_page;
          const metadataStartPage =
            metadataStartPageRaw != null &&
            !Number.isNaN(Number(metadataStartPageRaw))
              ? Number(metadataStartPageRaw)
              : null;

          const currentMetadata = useReaderStore.getState().metadata;
          const apiStartPage = resolveApiInitialStartPage(
            initialStartPage,
            currentMetadata,
          );
          const currentDocumentId = useReaderStore.getState().documentId;
          const localStorageKey = currentDocumentId
            ? `igp_reader_state_${currentDocumentId}`
            : null;
          const localRaw = localStorageKey
            ? localStorage.getItem(localStorageKey)
            : null;

          let startPageToApply =
            apiStartPage != null && apiStartPage > 0
              ? apiStartPage
              : metadataStartPage != null && metadataStartPage > 1
                ? metadataStartPage
                : null;

          try {
            if (localRaw) {
              const local = JSON.parse(localRaw);
              const savedPage =
                local.currentPage != null ? Number(local.currentPage) : null;
              const useSaved =
                (startPageToApply == null || startPageToApply <= 1) &&
                !isLegacySavedEpubPage(savedPage, currentMetadata);
              if (savedPage >= 1 && useSaved) {
                startPageToApply = savedPage;
              }
            }
          } catch (e) {
            // ignore parse error
          }

          const hasViewingRules =
            !!accessData?.drm_policy?.viewing_rules?.page_ranges?.length;
          const effectiveStartPage = hasViewingRules
            ? getEffectiveStartPage(accessData.drm_policy, startPageToApply)
            : startPageToApply != null
              ? startPageToApply
              : 1;

          if (pageDisplayStyle != null || effectiveStartPage != null) {
            setInitialView(pageDisplayStyle, effectiveStartPage);
          }

          setAnnotationData("bookmarks", bookmarks);
          setAnnotationData("highlights", highlights);
          setAnnotationData("notes", notes);
          setAnnotationData("freehand_drawing", drawings);
          setAnnotationData("underlines", underlines);
        } catch (err) {
          console.error("Error applying validation access data:", err);
          setAnnotationError(
            "bookmarks",
            err?.message || "Failed to initialize annotations",
          );
          setAnnotationError(
            "highlights",
            err?.message || "Failed to initialize highlights",
          );
          setAnnotationError(
            "notes",
            err?.message || "Failed to initialize notes",
          );
        } finally {
          setAnnotationLoading("bookmarks", false);
          setAnnotationLoading("highlights", false);
          setAnnotationLoading("notes", false);
        }

        setIsInitializing(false);
      } catch (err) {
        console.error("❌ Initialization error:", err);
        setError(
          err?.message || i18n.t("errors.failedToInitializeReader"),
        );
        setIsInitializing(false);
      } finally {
        setLoading(false);
      }
    },
    [
      setDocument,
      setTotalPages,
      setError,
      setLoading,
      setIsInitializing,
      setInitMessage,
      setAnnotationLoading,
      setAnnotationError,
      setDrmPolicy,
      setInitialView,
      setAnnotationData,
      fetchDocumentJson,
    ],
  );

  // Check sessionStorage for previous session first
  useEffect(() => {
    const savedSession = sessionStorage.getItem("igp_session");
    const hash = window.location.hash;

    if (hash && hash.startsWith("#token=")) {
      // Priority 1: Fresh JWT token in URL
      const token = hash.substring(7);
      if (token) {
        initializeReaderFromToken(token);
        // Clean URL hash without reloading
        window.history.replaceState(null, null, " ");
      }
    } else if (savedSession) {
      // Priority 2: Restore from previous session
      try {
        const session = JSON.parse(savedSession);
        initializeReaderFromSession(session);
      } catch (err) {
        console.error("Error parsing saved session:", err);
        sessionStorage.removeItem("igp_session");
        clearAiSessionCaches();
      }
    }
  }, [initializeReaderFromToken, initializeReaderFromSession]);

  if (isUnauthorizedDomain) {
    return <UnauthorizedDomain />;
  }

  return (
    <>
      <LoadingScreen show={isInitializing} message={initMessage} />
      <ReaderShell onToggleHelp={toggleHelp} />
      <Help show={isHelpOpen} onClose={toggleHelp} />
      <NotificationCenter />
    </>
  );
}
